import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { submitFeedback } from '../../thunks/feedbackThunks/thunk';
import { updateShowSuggestionModal } from '../../reducer/authSlice';
import { toast } from 'react-toastify';

const FeedbackModal = ({ open, onClose }) => {
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const userData = useSelector((state) => state.authSlice.userData);
  const dispatch = useDispatch();

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await submitFeedback(feedback.trim());
      toast.success('Thank you for your feedback!');
      setFeedback('');
      dispatch(updateShowSuggestionModal(false));
      if (onClose) onClose();
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true}
      disableBackdropClick={true}
      hideBackdrop={false}
      sx={{
        '& .MuiDialog-paper': {
          zIndex: 9999,
        },
        '& .MuiBackdrop-root': {
          zIndex: 9998,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" component="h2">
            We'd Love Your Feedback!
          </Typography>
          <Chip label="Required" color="error" size="small" variant="outlined" />
        </Box>
        <Typography variant="body2" color="textSecondary">
          Please provide your feedback to continue using the platform. This step cannot be skipped.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
            We're always looking to improve and your input is valuable to us. This feedback is
            required to continue.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          label="Your Feedback *"
          placeholder="Please share your thoughts, suggestions, or any issues you've encountered..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={loading}
          error={!!error}
          helperText={error || 'This field is required to continue'}
          required
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !feedback.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ minWidth: '120px' }}
        >
          {loading ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;
